/**
 * FastWayShare — transfer.js
 * Transfer orchestrator.
 * Manages the full lifecycle of sending/receiving files:
 *
 * NEW FEATURES:
 *   - Optional AES-256-GCM encryption (off by default, WebRTC DTLS always active)
 *   - Passphrase-based AES key derivation (PBKDF2)
 *   - Pause/cancel for both sender AND receiver
 *   - Receiver pause → sends PAUSE message to sender
 *   - Pre-buffering chunks during ICE negotiation
 *   - Chunk prioritization (high-priority chunks sent first)
 *   - Round-robin chunk distribution across parallel DataChannels
 *   - Adaptive chunk size via RTT monitor
 *   - Chunk deduplication via CHUNK_MAP on resume
 *   - Wake Lock integration for mobile
 *   - Auto-reconnect tracking
 */

import { MSG, CHUNK_SIZE } from '../utils/constants.js';
import {
  encryptChunk, decryptChunk, hashSHA256,
  deriveKeyFromPassphrase, generateSalt, passthroughChunk,
} from './crypto.js';
import { PrioritizedFileChunker, FileStreamWriter, compressToZip } from './chunker.js';
import { formatBytes } from '../utils/helpers.js';
import { saveTransferMeta, getSavedChunkCount, deleteTransfer, getAllChunks, getContiguousChunkCount } from '../utils/db.js';
import { RTTMonitor } from './adaptiveChunk.js';
import { settings } from './settings.js';

const SPEED_WINDOW = 3000;
const PIPELINE_BUFFER_SIZE = 32; // pre-buffer 32 chunks during ICE

// ─── Chunk Pipeline ───────────────────────────────────────────────────────────

class ChunkPipeline {
  #chunker;
  #encryptFn;
  #bufferSize;
  #queue = [];
  #error = null;
  #running = true;
  #fillPromise = null;
  #waiters = [];
  #spaceWaiters = [];

  constructor(chunker, encryptFn, bufferSize = PIPELINE_BUFFER_SIZE) {
    this.#chunker = chunker;
    this.#encryptFn = encryptFn;
    this.#bufferSize = bufferSize;
  }

  start() {
    this.#fillPromise = this.#fillBuffer();
  }

  async #fillBuffer() {
    try {
      while (this.#running && !this.#chunker.done) {
        if (this.#queue.length >= this.#bufferSize) {
          await new Promise(resolve => this.#spaceWaiters.push(resolve));
          continue;
        }

        const chunk = await this.#chunker.nextChunk();
        if (!chunk) break;

        const encryptPromise = (async () => {
          const encrypted = await this.#encryptFn(chunk.data);
          return {
            data: encrypted,
            isLast: chunk.isLast,
            offset: chunk.offset,
            length: chunk.data.byteLength,
            priority: chunk.priority ?? 'normal',
          };
        })();

        this.#queue.push(encryptPromise);

        if (this.#waiters.length > 0) {
          this.#waiters.shift()();
        }
      }
      while (this.#waiters.length > 0) {
        this.#waiters.shift()();
      }
    } catch (err) {
      this.#error = err;
      while (this.#waiters.length > 0) {
        this.#waiters.shift()();
      }
    }
  }

  async next() {
    if (this.#error) throw this.#error;
    while (this.#queue.length === 0) {
      if (this.#chunker.done) return null;
      if (this.#error) throw this.#error;
      await new Promise(resolve => this.#waiters.push(resolve));
    }

    const item = this.#queue.shift();
    if (this.#spaceWaiters.length > 0) {
      this.#spaceWaiters.shift()();
    }
    return item;
  }

  cancel() {
    this.#running = false;
    while (this.#waiters.length > 0) this.#waiters.shift()();
    while (this.#spaceWaiters.length > 0) this.#spaceWaiters.shift()();
  }
}

// ─── TransferManager ─────────────────────────────────────────────────────────

export class TransferManager extends EventTarget {
  /** @type {import('./webrtc.js').WebRTCManager} */
  #rtc;
  /** @type {CryptoKey|null} */
  #sharedKey = null;

  // Encryption mode
  #aesEnabled = false;
  #aesPassphrase = '';
  #aesKey = null;      // resolved AES key (from ECDH or PBKDF2)
  #passwordPromiseResolve = null;

  // Sender state
  #sendQueue = [];
  #currentSender = null;

  // Receiver state
  #currentReceiver = null;

  // Completed hashes in current session
  #completedHashes = new Set();

  // Pause/cancel flags
  #sendPaused = false;
  #sendCancelled = false;
  #recvPaused = false;
  #recvCancelled = false;

  // RTT monitor (adaptive chunk size)
  #rttMonitor = null;

  /**
   * @param {import('./webrtc.js').WebRTCManager} rtcManager
   */
  constructor(rtcManager) {
    super();
    this.#rtc = rtcManager;
    this.#setupListeners();
  }

  /** Set the ECDH-derived shared AES key */
  setSharedKey(key) {
    this.#sharedKey = key;
  }

  /**
   * Configure encryption mode from settings.
   * Must be called before starting a transfer.
   */
  async configureEncryption() {
    this.#aesEnabled = settings.get('aesEnabled');
    this.#aesPassphrase = settings.get('aesPassphrase') || '';

    if (!this.#aesEnabled) {
      this.#aesKey = null;
      return;
    }

    // If passphrase provided, PBKDF2 key will be derived when we know the salt
    // (salt is generated by sender and transmitted in FILE_OFFER)
    // For now just store the mode; key derivation happens in #sendNext or #handleFileOffer
    if (!this.#aesPassphrase) {
      // Use ECDH key (set via setSharedKey)
      this.#aesKey = this.#sharedKey;
    }
    // If passphrase is set, key derived later when salt is known
  }

  /**
   * Build the encrypt function for ChunkPipeline.
   * Returns async (data: ArrayBuffer) => ArrayBuffer
   */
  #getEncryptFn(aesKey) {
    if (!this.#aesEnabled || !aesKey) {
      return passthroughChunk;
    }
    return (data) => encryptChunk(data, aesKey);
  }

  /**
   * Build the decrypt function for received chunks.
   * Returns async (data: ArrayBuffer) => ArrayBuffer
   */
  #getDecryptFn(aesKey) {
    if (!this.#aesEnabled || !aesKey) {
      return passthroughChunk;
    }
    return (data) => decryptChunk(data, aesKey);
  }

  /** Start RTT monitoring for adaptive chunk size */
  startRTTMonitor() {
    if (this.#rttMonitor) {
      this.#rttMonitor.stop();
    }
    if (this.#rtc.pc) {
      this.#rttMonitor = new RTTMonitor(this.#rtc.pc);
      this.#rttMonitor.addEventListener('update', (e) => {
        const { optimalChunkSize } = e.detail;
        if (this.#currentSender?.chunker) {
          this.#currentSender.chunker.setChunkSize(optimalChunkSize);
        }
      });
      this.#rttMonitor.start();
    }
  }

  stopRTTMonitor() {
    this.#rttMonitor?.stop();
    this.#rttMonitor = null;
  }

  #setupListeners() {
    this.#rtc.addEventListener('message', (e) => this.#handleMessage(e.detail));
    this.#rtc.addEventListener('chunk', (e) => this.#handleChunk(e.detail));
    this.#rtc.addEventListener('datachannel_open', () => this.#handleDataChannelOpen());
    this.#rtc.addEventListener('disconnected', () => this.#handleDisconnect());
  }

  #handleDataChannelOpen() {
    if (this.#currentSender) {
      this.#rtc.sendMessage({ type: MSG.FILE_OFFER, meta: this.#currentSender.meta });
      this.#emit('offer_sent', { meta: this.#currentSender.meta });
    }
  }

  #handleDisconnect() {
    if (this.#currentSender) {
      this.#emit('progress', {
        filename: this.#currentSender.meta.filename,
        progress: this.#currentSender.bytesSent / this.#currentSender.meta.size,
        speed: 0, eta: Infinity,
        bytesSent: this.#currentSender.bytesSent,
        totalSize: this.#currentSender.meta.size,
        status: 'disconnected', direction: 'send',
      });
    } else if (this.#currentReceiver) {
      this.#emit('progress', {
        filename: this.#currentReceiver.meta.filename,
        progress: this.#currentReceiver.bytesReceived / this.#currentReceiver.meta.size,
        speed: 0, eta: Infinity,
        bytesReceived: this.#currentReceiver.bytesReceived,
        totalSize: this.#currentReceiver.meta.size,
        status: 'disconnected', direction: 'receive',
      });
    }
  }

  providePassword(pwd) {
    if (this.#passwordPromiseResolve) {
      this.#passwordPromiseResolve(pwd);
      this.#passwordPromiseResolve = null;
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Queue files for sending.
   * @param {File[]} files
   */
  async send(files) {
    if (files.length > 1) {
      this.#emit('progress', {
        filename: 'fastwayshare.zip',
        progress: 0, speed: 0, eta: Infinity,
        bytesSent: 0, totalSize: 0,
        status: 'zipping', direction: 'send',
      });

      try {
        const zipResult = await compressToZip(files, 'fastwayshare');
        const zipFile = new File([zipResult.data], 'fastwayshare.zip', { type: 'application/zip' });
        this.#sendQueue.push(zipFile);
      } catch (err) {
        console.error('Failed to zip files, sending individually:', err);
        this.#sendQueue.push(...files);
      }
    } else {
      this.#sendQueue.push(...files);
    }

    if (!this.#currentSender) {
      await this.#sendNext();
    }
  }

  async #sendNext() {
    if (this.#sendQueue.length === 0) {
      this.#emit('queue_complete');
      return;
    }
    const file = this.#sendQueue.shift();
    const chunker = new PrioritizedFileChunker(file);

    this.#emit('progress', {
      filename: file.name,
      progress: 0, speed: 0, eta: Infinity,
      bytesSent: 0, totalSize: file.size,
      status: 'hashing', direction: 'send',
    });

    const hash = await chunker.computeHash();

    let aesSalt = null;
    let aesKey = null;
    let aesTest = null;

    if (this.#aesEnabled) {
      if (this.#aesPassphrase) {
        aesSalt = generateSalt();
        aesKey = await deriveKeyFromPassphrase(this.#aesPassphrase, aesSalt);
        const magic = new TextEncoder().encode('FASTWAYSHARE_OK');
        const encryptedMagic = await encryptChunk(magic.buffer, aesKey);
        aesTest = Array.from(new Uint8Array(encryptedMagic));
      } else {
        aesKey = this.#sharedKey;
      }
    }
    this.#aesKey = aesKey;

    const meta = {
      filename: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      totalChunks: chunker.totalChunks,
      hash,
      // Encryption metadata
      aesEnabled: this.#aesEnabled,
      aesSalt: aesSalt ? Array.from(aesSalt) : null, // serialize for JSON
      aesTest: aesTest,
    };

    this.#currentSender = {
      chunker,
      meta,
      file,
      aesKey,
      startTime: Date.now(),
      bytesSent: 0,
      lastSpeedUpdate: Date.now(),
      bytesAtLastUpdate: 0,
      speed: 0,
    };

    // Start pre-buffering pipeline BEFORE DataChannel might be open
    // (pipeline starts reading file + encrypting while ICE negotiates)
    const encryptFn = this.#getEncryptFn(aesKey);
    const pipeline = new ChunkPipeline(chunker, encryptFn, PIPELINE_BUFFER_SIZE);
    this.#currentSender.pipeline = pipeline;
    pipeline.start();

    // Send FILE_OFFER to receiver (if DataChannel already open)
    this.#rtc.sendMessage({ type: MSG.FILE_OFFER, meta });
    this.#emit('offer_sent', { meta });
  }

  async #sendChunks(resumeOffset = 0, knownChunks = null) {
    const s = this.#currentSender;
    if (!s) return;

    // If resuming, rebuild pipeline from resume offset
    if (resumeOffset > 0) {
      s.pipeline?.cancel();
      s.chunker.seek(resumeOffset);
      s.bytesSent = resumeOffset;

      const encryptFn = this.#getEncryptFn(s.aesKey);
      const pipeline = new ChunkPipeline(s.chunker, encryptFn, PIPELINE_BUFFER_SIZE);
      s.pipeline = pipeline;
      pipeline.start();
    } else {
      s.bytesSent = 0;
    }

    this.#sendCancelled = false;
    this.#sendPaused = false;

    // Build set of chunks to skip (deduplication via CHUNK_MAP)
    const skipChunks = new Set(knownChunks ?? []);

    try {
      while (!this.#sendCancelled) {
        if (!this.#rtc.isOpen) break;

        // Sender pause (local or from receiver's PAUSE message)
        if (this.#sendPaused) {
          await new Promise(resolve => {
            const handler = () => { resolve(); this.removeEventListener('_send_resume', handler); };
            this.addEventListener('_send_resume', handler);
          });
        }

        const chunkPromise = await s.pipeline.next();
        if (!chunkPromise) break;

        const chunk = await chunkPromise;
        if (!this.#rtc.isOpen) break;

        // Skip if receiver already has this chunk (dedup)
        const chunkIndex = Math.floor(chunk.offset / (s.chunker.chunkSize || CHUNK_SIZE));
        if (skipChunks.has(chunkIndex)) {
          s.bytesSent = chunk.offset + chunk.length;
          continue;
        }

        // Flow control — wait for buffer capacity
        await this.#rtc.waitForAnyBuffer();
        if (!this.#rtc.isOpen) break;

        // Send via round-robin across parallel channels
        this.#rtc.sendDataRoundRobin(chunk.data);
        s.bytesSent = chunk.offset + chunk.length;

        // Speed tracking
        const now = Date.now();
        const elapsed = (now - s.lastSpeedUpdate) / 1000;
        if (elapsed >= 0.5) {
          const bytesDelta = s.bytesSent - s.bytesAtLastUpdate;
          s.speed = bytesDelta / elapsed;
          s.lastSpeedUpdate = now;
          s.bytesAtLastUpdate = s.bytesSent;
        }

        const progress = s.bytesSent / s.meta.size;
        const eta = s.speed > 0 ? (s.meta.size - s.bytesSent) / s.speed : Infinity;
        this.#emit('progress', {
          filename: s.meta.filename,
          bytesSent: s.bytesSent,
          totalSize: s.meta.size,
          progress, speed: s.speed, eta,
          direction: 'send', status: 'transferring',
        });

        if (chunk.isLast) {
          this.#rtc.sendMessage({
            type: MSG.TRANSFER_COMPLETE,
            hash: s.meta.hash,
            filename: s.meta.filename,
          });
          break;
        }
      }
    } catch (err) {
      console.error('Error in sendChunks pipeline:', err);
      this.#emit('error', { error: err.message });
    } finally {
      s.pipeline?.cancel();
    }
  }

  // ─── RECEIVER ──────────────────────────────────────────────────────────────

  async #handleMessage(msg) {
    switch (msg.type) {
      case MSG.FILE_OFFER:
        await this.#handleFileOffer(msg.meta);
        break;
      case MSG.TRANSFER_COMPLETE:
        await this.#handleTransferComplete(msg);
        break;
      case MSG.RESUME_REQUEST:
        await this.#sendChunks(msg.offset, msg.knownChunks ?? null);
        break;
      case MSG.TRANSFER_COMPLETE_ACK:
        await this.#handleTransferCompleteAck(msg.hash);
        break;
      case MSG.CANCEL:
        this.#handleRemoteCancel();
        break;
      case MSG.PAUSE:
        // Receiver asked sender to pause
        this.#sendPaused = true;
        this.#emit('paused');
        break;
      case MSG.RESUME:
        // Receiver asked sender to resume
        this.#sendPaused = false;
        this.dispatchEvent(new Event('_send_resume'));
        this.#emit('resumed');
        break;
      case MSG.RTT_PING:
        this.#rtc.sendMessage({ type: MSG.RTT_PONG, id: msg.id, ts: msg.ts });
        break;
    }
  }

  #handleRemoteCancel() {
    this.#sendCancelled = true;
    this.#recvCancelled = true;
    if (this.#currentSender?.pipeline) {
      this.#currentSender.pipeline.cancel();
    }
    if (this.#currentReceiver) {
      deleteTransfer(this.#currentReceiver.meta.hash).catch(console.error);
      this.#currentReceiver = null;
    }
    this.#emit('cancelled');
  }

  async #handleTransferCompleteAck(hash) {
    try {
      const s = this.#currentSender;
      if (s && s.meta.hash === hash) {
        this.#emit('send_complete', { meta: s.meta, duration: (Date.now() - s.startTime) / 1000 });
        this.#currentSender = null;
        await this.#sendNext();
      }
    } catch (err) {
      this.#emit('error', { error: err.message });
      this.#currentSender = null;
    }
  }

  async #handleFileOffer(meta) {
    if (this.#completedHashes.has(meta.hash)) {
      this.#rtc.sendMessage({ type: MSG.TRANSFER_COMPLETE_ACK, hash: meta.hash });
      return;
    }

    this.#emit('file_offered', { meta });
    this.#recvCancelled = false;
    this.#recvPaused = false;

    await saveTransferMeta(meta);

    const writer = new FileStreamWriter(meta.filename, meta.size, meta.hash);
    const savedChunks = await writer.init();
    const isResuming = savedChunks > 0;
    const initialBytes = Math.min(savedChunks * CHUNK_SIZE, meta.size);

    // Resolve AES key for decryption
    let aesKey = null;
    if (meta.aesEnabled) {
      if (meta.aesSalt) {
        let isError = false;
        while (true) {
          // We need the passphrase. Pause and ask the UI.
          this.#emit('needs_password', { error: isError });
          const pwd = await new Promise(resolve => {
            this.#passwordPromiseResolve = resolve;
          });
          
          // If user cancelled, pwd might be null, but let's assume they provide one or cancel the whole transfer
          if (!pwd) {
            this.#emit('cancelled');
            return;
          }

          const salt = new Uint8Array(meta.aesSalt);
          aesKey = await deriveKeyFromPassphrase(pwd, salt);
          
          if (meta.aesTest) {
            try {
              const testBuf = new Uint8Array(meta.aesTest).buffer;
              await decryptChunk(testBuf, aesKey);
              // Password is correct
              break;
            } catch (err) {
              // Wrong password! Loop again.
              isError = true;
            }
          } else {
            break;
          }
        }
      } else {
        aesKey = this.#sharedKey;
      }
    }
    this.#aesEnabled = meta.aesEnabled ?? false;
    this.#aesKey = aesKey;

    this.#currentReceiver = {
      writer,
      meta,
      bytesReceived: initialBytes,
      startTime: Date.now(),
      lastSpeedUpdate: Date.now(),
      bytesAtLastUpdate: initialBytes,
      speed: 0,
      chunksProcessed: savedChunks,
      transferCompleteMessageReceived: null,
      decryptFn: this.#getDecryptFn(aesKey),
    };

    if (isResuming) {
      // Send chunk map for deduplication (which chunks we already have)
      const knownChunks = [];
      for (let i = 0; i < savedChunks; i++) knownChunks.push(i);

      this.#rtc.sendMessage({
        type: MSG.RESUME_REQUEST,
        offset: initialBytes,
        knownChunks,
      });
      this.#emit('receive_started', { meta, isResuming: true, bytesReceived: initialBytes });
    } else {
      this.#rtc.sendMessage({ type: MSG.FILE_ACCEPT, filename: meta.filename });
      this.#emit('receive_started', { meta, isResuming: false, bytesReceived: 0 });
    }
  }

  async #handleChunk(rawData) {
    const r = this.#currentReceiver;
    if (!r) return;
    if (this.#recvCancelled) return;

    // Receiver pause: buffer the chunk and wait
    if (this.#recvPaused) {
      await new Promise(resolve => {
        const handler = () => { resolve(); this.removeEventListener('_recv_resume', handler); };
        this.addEventListener('_recv_resume', handler);
      });
    }

    try {
      const decrypted = await r.decryptFn(rawData);
      await r.writer.write(decrypted);
      r.bytesReceived += decrypted.byteLength;

      const now = Date.now();
      const elapsed = (now - r.lastSpeedUpdate) / 1000;
      if (elapsed >= 0.5) {
        const delta = r.bytesReceived - r.bytesAtLastUpdate;
        r.speed = delta / elapsed;
        r.lastSpeedUpdate = now;
        r.bytesAtLastUpdate = r.bytesReceived;
      }

      const progress = r.bytesReceived / r.meta.size;
      const eta = r.speed > 0 ? (r.meta.size - r.bytesReceived) / r.speed : Infinity;
      this.#emit('progress', {
        filename: r.meta.filename,
        bytesReceived: r.bytesReceived,
        totalSize: r.meta.size,
        progress, speed: r.speed, meta: r.meta, eta,
        direction: 'receive', status: 'transferring',
      });

      r.chunksProcessed++;
      if (r.transferCompleteMessageReceived && r.chunksProcessed === r.meta.totalChunks) {
        await this.#finalizeReceiver(r, r.transferCompleteMessageReceived);
      }
    } catch (e) {
      console.error('Decryption/write error in handleChunk:', e);
      this.#emit('decryption_error', { error: e.message });
      this.cancelReceive();
    }
  }

  async #handleTransferComplete(msg) {
    const r = this.#currentReceiver;
    if (!r) return;
    r.transferCompleteMessageReceived = msg;
    if (r.chunksProcessed === r.meta.totalChunks) {
      await this.#finalizeReceiver(r, msg);
    }
  }

  async #finalizeReceiver(r, msg) {
    try {
      const { receivedHash, hashMatch, blob } = await r.writer.finalize(r.meta.mimeType, msg.hash);
      this.#completedHashes.add(r.meta.hash);
      this.#rtc.sendMessage({ type: MSG.TRANSFER_COMPLETE_ACK, hash: r.meta.hash });
      this.#emit('receive_complete', {
        meta: r.meta, hashMatch, receivedHash,
        expectedHash: msg.hash,
        duration: (Date.now() - r.startTime) / 1000,
        blob,
      });
    } catch (err) {
      this.#emit('error', { error: `Finalization error: ${err.message}` });
    } finally {
      this.#currentReceiver = null;
    }
  }

  // ─── CONTROLS ──────────────────────────────────────────────────────────────

  /** Called when sender receives FILE_ACCEPT — start sending chunks */
  onAccepted() {
    this.#sendChunks(0);
  }

  // --- Sender controls ---

  pauseSend() {
    this.#sendPaused = true;
    this.#emit('paused', { direction: 'send' });
  }

  resumeSend() {
    this.#sendPaused = false;
    this.dispatchEvent(new Event('_send_resume'));
    this.#emit('resumed', { direction: 'send' });
  }

  cancelSend() {
    this.#sendCancelled = true;
    if (this.#currentSender?.pipeline) {
      this.#currentSender.pipeline.cancel();
    }
    this.#rtc.sendMessage({ type: MSG.CANCEL });
    this.#currentSender = null;
    this.#emit('cancelled', { direction: 'send' });
  }

  // --- Receiver controls ---

  pauseReceive() {
    this.#recvPaused = true;
    // Ask sender to stop sending
    this.#rtc.sendMessage({ type: MSG.PAUSE });
    this.#emit('paused', { direction: 'receive' });
  }

  resumeReceive() {
    this.#recvPaused = false;
    this.dispatchEvent(new Event('_recv_resume'));
    // Ask sender to resume
    this.#rtc.sendMessage({ type: MSG.RESUME });
    this.#emit('resumed', { direction: 'receive' });
  }

  cancelReceive() {
    this.#recvCancelled = true;
    if (this.#currentReceiver) {
      deleteTransfer(this.#currentReceiver.meta.hash).catch(console.error);
      this.#currentReceiver = null;
    }
    this.#rtc.sendMessage({ type: MSG.CANCEL });
    this.#emit('cancelled', { direction: 'receive' });
  }

  // --- Legacy API (backwards compat) ---

  pause() { this.pauseSend(); }

  resume() { this.resumeSend(); }

  cancel() {
    this.#sendCancelled = true;
    this.#recvCancelled = true;
    if (this.#currentSender?.pipeline) {
      this.#currentSender.pipeline.cancel();
    }
    if (this.#currentReceiver) {
      deleteTransfer(this.#currentReceiver.meta.hash).catch(console.error);
      this.#currentReceiver = null;
    }
    this.#rtc.sendMessage({ type: MSG.CANCEL });
    this.#emit('cancelled');
  }

  #emit(type, detail = null) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }
}
